import { createNotification } from "react-redux-notify";
import { select, put, takeLatest, all } from "redux-saga/effects";
import {
  userUnsubscribeSuccess,
  userUnsubscribeFailed,
  fetchSubscriptionSuccess,
  fetchSubscriptionFailure,
  setSubscriptionPriceFailed,
  setSubscriptionBundleFailed,
  setSubscriptionPriceSuccess,
  setSubscriptionBundleSuccess,
  fetchSingleSubscriptionSuccess,
  fetchSingleSubscriptionFailure,
  subscriptionAutoRenewalSuccess,
  subscriptionAutoRenewalFailure,
  subscriptionPaymentStripeFailure,
  subscriptionPaymentStripeSuccess,
  subscriptionPaymentWalletSuccess,
  subscriptionPaymentWalletFailure,
  subscriptionPaymentPaypalSuccess,
  subscriptionPaymentPaypalFailure,
  fetchMyActiveSubscriptionSuccess,
  fetchActiveSubscribersSuccess,
} from "../actions/SubscriptionAction";
import {
  USER_UNSUBSCRIBE_START,
  SUBSCRIBE_TO_PLAN_START,
  TOGGLE_SUBSCRIPTION_FREE,
  FETCH_SUBSCRIPTION_START,
  CANCEL_SUBSCRIPTION_START,
  SET_SUBSCRIPTION_FREE_START,
  SET_SUBSCRIPTION_PRICE_START,
  SET_SUBSCRIPTION_BUNDLE_START,
  FETCH_SUBSCRIPTION_PLAN_START,
  DELETE_SUBSCRIPTION_PLAN_START,
  FETCH_SINGLE_SUBSCRIPTION_START,
  SUBSCRIPTION_AUTO_RENEWAL_START,
  SUBSCRIPTION_PAYMENT_STRIPE_START,
  SUBSCRIPTION_PAYMENT_WALLET_START,
  SUBSCRIPTION_PAYMENT_PAYPAL_START,
  IS_SUBSCRIBED_TO_START,
  FETCH_PERFORMER_SUBSCRIPTION_PLAN_START,
  FETCH_MY_ACTIVE_SUBSCRIPTION_START,
  FETCH_ACTIVE_SUBSCRIBERS_START,
} from "../actions/ActionConstant";
import {
  getSuccessNotificationMessage,
  getErrorNotificationMessage,
} from "../../components/helper/NotificationMessage";
import api from "../../Environment";
import ErrorUtils from "../../helpers/utils/errors";
import { fetchWalletDetailsStart } from "../actions/WalletAction";
import { fetchSingleUserProfileStart } from "../actions/OtherUserAction";
import {
  setSubscriptionFreeFailed,
  fetchSubscriptionPlanStart,
  setSubscriptionFreeSuccess,
  fetchSubscriptionPlanSuccess,
  fetchSubscriptionPlanFailure,
  deleteSubscriptionPlanSuccess,
  isSubscribedToSuccess,
  isSubscribedToFailure,
  cancelSubscriptionSuccess,
  cancelSubscriptionFailure,
  subscribeToPlanSuccess,
  subscribeToPlanFailure,
} from "../actions/SubscriptionPaymentAction";
import { parsePaymentServiceError } from "../../helpers/services/payment/error";
import subscriptionService from "../../helpers/services/payment/subscription.service";
import { showErrorComponent } from "../actions/ComponentAction";
import { setHomePost } from "../actions/HomeAction";
import { setSinglePost, setUserPost } from "../actions/PostAction";
import { setOtherUserSinglePost } from "../actions/OtherUserAction";
import { setBookmarkPost } from "../actions/BookmarkAction";
import { findPostUniqueIdByPostId } from "../../components/helper/Utils";

/**
 * Helper function to fetch and update post data after subscription
 * Subscription API returns post_id, but posts_view_for_others needs post_unique_id
 * So we need to find the post in Redux store by post_id to get its post_unique_id
 * @param {number|string} postId - The post_id returned from subscription API
 */
function* fetchAndUpdatePostAfterSubscription(postId) {
  if (!postId) return;
  
  // Find post in Redux store by post_id to get post_unique_id
  const state = yield select();
  const postUniqueId = findPostUniqueIdByPostId(state, postId);
  
  // If we found post_unique_id, fetch the post using it
  if (postUniqueId) {
    try {
      const response = yield api.postMethod("posts_view_for_others", {
        post_unique_id: postUniqueId,
      });
      
      if (response.data.success && response.data.data?.post) {
        // Update post in all relevant reducers
        yield put(setHomePost(response.data.data)); // Home page posts
        yield put(setSinglePost(response.data.data)); // Single post page
        yield put(setOtherUserSinglePost(response.data.data)); // Profile posts page
        yield put(setUserPost(response.data.data)); // Profile posts in PostReducer
        yield put(setBookmarkPost(response.data.data)); // Bookmarks page (all, photo, video)
      }
    } catch (error) {
      // Silently fail - don't interrupt the subscription flow
      console.error('Failed to fetch post after subscription:', error);
    }
  } else {
    console.warn(`Could not find post_unique_id for post_id: ${postId} in Redux store`);
  }
}

function* getSubscriptionAPI() {
  try {
    const response = yield api.postMethod("subscriptions_index");
    yield put(fetchSubscriptionSuccess(response.data.data));
    if (response.data.success) {
      // Do nothing
    } else {
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(fetchSubscriptionFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* getSingleSubscriptionAPI() {
  try {
    const subscriptionInputData = yield select(
      (state) => state.subscriptions.singleSubInputData.data
    );
    const response = yield api.postMethod(
      "subscriptions_view",
      subscriptionInputData
    );
    yield put(fetchSingleSubscriptionSuccess(response.data.data));
    if (response.data.success) {
      // Do nothing
    } else {
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(fetchSingleSubscriptionFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* subscriptionPaymentStripeAPI() {
  try {
    const subscriptioDetails = yield select(
      (state) => state.subscriptions.subPayStripe.inputData
    );
    const response = yield api.postMethod(
      "user_subscriptions_payment_by_stripe",
      subscriptioDetails
    );
    if (response.data.success) {
      yield put(subscriptionPaymentStripeSuccess(response.data.data));
      const notificationMessage = getSuccessNotificationMessage(
        response.data.message
      );
      yield put(createNotification(notificationMessage));
      window.location.assign(`${subscriptioDetails.user_unique_id}`);
    } else {
      yield put(subscriptionPaymentStripeFailure(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(subscriptionPaymentStripeFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* subscriptionPaymentPaypalAPI() {
  try {
    const subscriptioDetails = yield select(
      (state) => state.subscriptions.subPayPaypal.inputData
    );
    const response = yield api.postMethod(
      "user_subscriptions_payment_by_paypal",
      subscriptioDetails
    );
    if (response.data.success) {
      yield put(subscriptionPaymentPaypalSuccess(response.data.data));
      const notificationMessage = getSuccessNotificationMessage(
        response.data.message
      );
      yield put(createNotification(notificationMessage));
      window.location.assign(`${subscriptioDetails.user_unique_id}`);
    } else {
      yield put(subscriptionPaymentPaypalFailure(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(subscriptionPaymentPaypalFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* subscriptionPaymentWalletAPI() {
  try {
    const subscriptioDetails = yield select(
      (state) => state.subscriptions.subPayWallet.inputData
    );
    const response = yield api.postMethod(
      "user_subscriptions_payment_through_wallet",
      subscriptioDetails
    );

    if (response.data.success) {
      yield put(subscriptionPaymentWalletSuccess(response.data.data));
      yield put(fetchWalletDetailsStart());
      const notificationMessage = getSuccessNotificationMessage(
        response.data.message
      );
      yield put(createNotification(notificationMessage));
      window.location.assign(`/user/${subscriptioDetails?.username?.toLowerCase()}`);
    } else {
      yield put(subscriptionPaymentWalletFailure(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(subscriptionPaymentWalletFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* subscriptionAutoRenewalAPI() {
  try {
    const subscriptioDetails = yield select(
      (state) => state.subscriptions.subscriptionRenew.inputData
    );
    const response = yield api.postMethod(
      "subscriptions_autorenewal_status",
      subscriptioDetails
    );
    yield put(subscriptionAutoRenewalSuccess(response.data.data));
    if (response.data.success) {
      const notificationMessage = getSuccessNotificationMessage(
        response.data.message
      );
      yield put(createNotification(notificationMessage));
      yield put(subscriptionAutoRenewalFailure(response.data.error));
    } else {
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(subscriptionAutoRenewalFailure(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* createSubscriptionPlanAPI() {
  try {
    const bundleDetails = yield select(
      (state) => state.subscriptions.subscriptionBundle.inputData
    );
    const response = yield subscriptionService.createSubscriptionPlan(
      bundleDetails
    );

    if (response.success) {
      yield put(setSubscriptionBundleSuccess(response));
      yield put(fetchSubscriptionPlanStart());
      const notificationMessage = getSuccessNotificationMessage(
        response.message
      );
      yield put(createNotification(notificationMessage));
    } else {
      yield put(setSubscriptionBundleFailed(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

//set subscription price

function* setSubscriptionPriceAPI(action) {
  try {
    const response = yield subscriptionService.updateDefaultSubscriptionPrice(
      Number(action.data.amount)
    );
    if (response.success) {
      yield put(setSubscriptionPriceSuccess(response.data));
      yield put(fetchSubscriptionPlanStart());
      const notificationMessage = getSuccessNotificationMessage(
        response.message
      );
      yield put(createNotification(notificationMessage));
    } else {
      yield put(setSubscriptionPriceFailed(response.error));
      const notificationMessage = getErrorNotificationMessage(response.error);
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    yield put(setSubscriptionPriceFailed(message));
    const notificationMessage = getErrorNotificationMessage(message);
    yield put(createNotification(notificationMessage));
  }
}

//unsubscribe

function* userUnsubscribeAPI() {
  try {
    const inputData = yield select(
      (state) => state.subscriptions.unsubscribe.inputData
    );
    const response = yield api.postMethod("user_unsubscribe", inputData);
    if (response.data.success) {
      yield put(userUnsubscribeSuccess(response.data.data));
      const notificationMessage = getSuccessNotificationMessage(
        response.data.message
      );
      yield put(createNotification(notificationMessage));
      const user_unique_id = yield select(
        (state) => state.otherUser.userDetails.data.user.user_unique_id
      );
      yield put(fetchSingleUserProfileStart({ user_unique_id }));
    } else {
      yield put(userUnsubscribeFailed(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(userUnsubscribeFailed(error));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* fetchSubscriptionPlansAPI() {
  try {
    const response = yield subscriptionService.getSubscriptionPlans();
    yield put(fetchSubscriptionPlanSuccess(response));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

function* fetchPerformerSubscriptionPlansAPI(action) {
  try {
    const response = yield subscriptionService.getSubscriptionPlansForUser(
      action.data
    );
    yield put(fetchSubscriptionPlanSuccess(response));
  } catch (error) {
    const { notify, message } = ErrorUtils.handlePaymentError(error.response)
    yield put(fetchSubscriptionPlanFailure(message));
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    if (notify) {
      yield put(createNotification(getErrorNotificationMessage(message)));
    }
  }
}

function* deleteSubscriptionPlanAPI(action) {
  try {
    const response = yield subscriptionService.deleteSubscriptionPlan(
      action.data
    );
    yield put(deleteSubscriptionPlanSuccess(response));
    yield put(fetchSubscriptionPlanStart());
    const notificationMessage = getSuccessNotificationMessage(response.message);
    yield put(createNotification(notificationMessage));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

function* subscribeToPlanAPI(action) {
  try {
    const response = yield subscriptionService.subscribeToPlan(action.data?.planId, action.data?.postId);

    yield put(subscribeToPlanSuccess(response.data));
    yield put(fetchWalletDetailsStart());

    // Fetch and update post if post_id is returned
    // Note: subscription API returns post_id, but posts_view_for_others needs post_unique_id
    // So we need to find the post in Redux store to get post_unique_id
    const postId = response.data?.post_id || action.data?.postId;
    if (postId) {
      yield* fetchAndUpdatePostAfterSubscription(postId);
    }

    if (action.data.refresh) window.location.reload();

    const notificationMessage = getSuccessNotificationMessage(response.message);
    yield put(createNotification(notificationMessage));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(subscribeToPlanFailure(message));
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

function* isSubscribedToAPI(action) {
  try {
    const response = yield subscriptionService.isSubscribedTo(action.data);
    yield put(isSubscribedToSuccess(response.data));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    yield put(isSubscribedToFailure(message));
  }
}

/**
 * cancel the selected subscription
 * @param {*} action
 */
function* cancelSubscriptionAPI(action) {
  try {
    const response = yield subscriptionService.cancelSubscription(
      action.data.unique_id
    );
    yield put(cancelSubscriptionSuccess(response))

    const notificationMessage = getSuccessNotificationMessage(response.message);
    yield put(createNotification(notificationMessage));
    window.location.reload();
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(cancelSubscriptionFailure(message))
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

// set subscription free

function* setSubscriptionFreeAPI(action) {
  try {
    const response = yield subscriptionService.setSubscriptionFree(action.data);
    yield put(setSubscriptionFreeSuccess(response.data));
    const notificationMessage = getSuccessNotificationMessage(response.message);
    yield put(createNotification(notificationMessage));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(setSubscriptionFreeFailed(message));
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

// turn off free subscription

function* setSubscriptionPaidAPI(action) {
  const is_first_open = yield select((state) => state.subscriptionsPayment.onBoardSubscription.is_first_open);
  if (!action.data && !is_first_open) {
    try {
      const amount = yield select(
        (state) =>
          state.subscriptionsPayment.subscriptionPlans.data.default.amount
      );
      const response = yield subscriptionService.updateDefaultSubscriptionPrice(
        Number(amount)
      );
      yield put(setSubscriptionPriceSuccess(response.data));
      yield put(fetchSubscriptionPlanStart());
      const notificationMessage = getSuccessNotificationMessage(
        "Paid Subscription enabled successfully"
      );
      yield put(createNotification(notificationMessage));
    } catch (error) {
      const message = parsePaymentServiceError(error.response);
      if(error?.response?.status !== 400){
        yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
      }
      yield put(createNotification(getErrorNotificationMessage(message)));
    }
  }
}

function* getMyActiveSubscriptionAPI() {
  try {
    const inputData = yield select((state) => state.subscriptions.myActiveSubscription.inputData);
    const limit = yield select((state) => state.subscriptions.myActiveSubscription.limit);

    const response = yield subscriptionService.getMyActiveSubscriptions({
      ...inputData,
      limit,
    });
    yield put(fetchMyActiveSubscriptionSuccess(response.data));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

function* getActiveSubscribersAPI() {
  try {
    const inputData = yield select((state) => state.subscriptions.subscribers.inputData);
    const limit = yield select((state) => state.subscriptions.subscribers.limit);

    const response = yield subscriptionService.getActiveSubscribers({
      ...inputData,
      limit,
    });

    yield put(fetchActiveSubscribersSuccess(response.data));
  } catch (error) {
    const message = parsePaymentServiceError(error.response);
    if(error?.response?.status !== 400){
      yield put(showErrorComponent({error:error, status:error?.response?.status ?  error?.response?.status : 500}));
    }
    yield put(createNotification(getErrorNotificationMessage(message)));
  }
}

export default function* pageSaga() {
  yield all([yield takeLatest(FETCH_SUBSCRIPTION_START, getSubscriptionAPI)]);
  yield all([
    yield takeLatest(FETCH_SINGLE_SUBSCRIPTION_START, getSingleSubscriptionAPI),
  ]);
  yield all([
    yield takeLatest(
      SUBSCRIPTION_PAYMENT_STRIPE_START,
      subscriptionPaymentStripeAPI
    ),
  ]);
  yield all([
    yield takeLatest(
      SUBSCRIPTION_PAYMENT_WALLET_START,
      subscriptionPaymentWalletAPI
    ),
  ]);
  yield all([
    yield takeLatest(
      SUBSCRIPTION_AUTO_RENEWAL_START,
      subscriptionAutoRenewalAPI
    ),
  ]);
  yield all([
    yield takeLatest(
      SUBSCRIPTION_PAYMENT_PAYPAL_START,
      subscriptionPaymentPaypalAPI
    ),
  ]);
  yield all([
    yield takeLatest(SET_SUBSCRIPTION_PRICE_START, setSubscriptionPriceAPI),
  ]);
  yield all([yield takeLatest(USER_UNSUBSCRIBE_START, userUnsubscribeAPI)]);

  //Subscription Payments Service
  yield all([
    yield takeLatest(FETCH_SUBSCRIPTION_PLAN_START, fetchSubscriptionPlansAPI),
  ]);
  yield all([
    yield takeLatest(SET_SUBSCRIPTION_BUNDLE_START, createSubscriptionPlanAPI),
  ]);
  yield all([
    yield takeLatest(DELETE_SUBSCRIPTION_PLAN_START, deleteSubscriptionPlanAPI),
  ]);
  yield all([
    yield takeLatest(
      FETCH_PERFORMER_SUBSCRIPTION_PLAN_START,
      fetchPerformerSubscriptionPlansAPI
    ),
  ]);
  yield all([yield takeLatest(SUBSCRIBE_TO_PLAN_START, subscribeToPlanAPI)]);
  yield all([
    yield takeLatest(
      IS_SUBSCRIBED_TO_START,
      isSubscribedToAPI
    ),
  ]);
  yield all([
    yield takeLatest(CANCEL_SUBSCRIPTION_START, cancelSubscriptionAPI),
  ]);
  yield all([
    yield takeLatest(SET_SUBSCRIPTION_FREE_START, setSubscriptionFreeAPI),
  ]);
  yield all([
    yield takeLatest(TOGGLE_SUBSCRIPTION_FREE, setSubscriptionPaidAPI),
  ]);
  yield all([
    yield takeLatest(
      FETCH_MY_ACTIVE_SUBSCRIPTION_START,
      getMyActiveSubscriptionAPI
    )
  ])
  yield all([
    yield takeLatest(
      FETCH_ACTIVE_SUBSCRIBERS_START, 
      getActiveSubscribersAPI
    )
  ])
}
